// CancelPendingModal — required reason textarea + cancel mutation. Mirrors
// the legacy `cancel_pending_booking.php` flow: a "Booking cancelled. Reason:
// {reason}" note is appended server-side.
//
// Server enforces a min length of 10 characters; the client mirrors this so
// the user gets immediate feedback before submit.

import { useEffect, useState } from 'react';
import axios from 'axios';
import { Modal } from '../../../components/ui/Modal/Modal';
import { Button } from '../../../components/ui/Button/Button';
import { Textarea } from '../../../components/form/Textarea';
import { useToast } from '../../../hooks/useToast';
import { useCancelPending } from '../hooks/usePending';
import styles from './CancelPendingModal.module.css';

const REASON_MIN = 10;
const REASON_MAX = 2000;

export interface CancelPendingModalProps {
  open: boolean;
  encId: string | null;
  reference?: string | null;
  onClose: () => void;
  onCancelled?: () => void;
}

export function CancelPendingModal({
  open,
  encId,
  reference,
  onClose,
  onCancelled,
}: CancelPendingModalProps): JSX.Element | null {
  const toast = useToast();
  const [reason, setReason] = useState('');
  const cancel = useCancelPending(encId ?? undefined);
  const submitting = cancel.isPending;

  useEffect(() => {
    if (open) setReason('');
  }, [open, encId]);

  if (!open) return null;

  const reasonLength = reason.trim().length;
  const tooShort = reasonLength < REASON_MIN;
  const tooLong = reason.length > REASON_MAX;
  const canSubmit = !tooShort && !tooLong && !submitting && encId !== null;

  async function handleConfirm() {
    if (!canSubmit || !encId) return;
    try {
      await cancel.mutateAsync({ reason: reason.trim() });
      toast.success('Pending booking cancelled.');
      onCancelled?.();
      onClose();
    } catch (err) {
      const msg = axios.isAxiosError(err)
        ? (err.response?.data as { message?: string } | undefined)?.message ??
          'Could not cancel pending booking.'
        : 'Could not cancel pending booking.';
      toast.error(msg);
    }
  }

  return (
    <Modal
      open={open}
      onClose={submitting ? () => undefined : onClose}
      title="Cancel Pending Booking"
      size="md"
      preventClose={submitting}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Keep pending
          </Button>
          <Button
            variant="danger"
            onClick={handleConfirm}
            loading={submitting}
            disabled={!canSubmit}
          >
            Cancel Booking
          </Button>
        </>
      }
    >
      {reference ? (
        <p className={styles.subtitle}>Pending {reference}</p>
      ) : null}

      <div className={styles.warningBox} role="note">
        This booking will be marked as cancelled and moved out of the pending
        list.
      </div>

      <div className={styles.field}>
        <label className={styles.label} htmlFor="cancel-pending-reason">
          Cancellation Reason <span aria-hidden>*</span>
        </label>
        <Textarea
          id="cancel-pending-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={4}
          autoResize
          placeholder="Please provide a reason for cancellation (e.g. customer changed mind, pricing issue, etc.)"
          aria-required="true"
          aria-invalid={tooShort || tooLong || undefined}
        />
        <span
          className={
            tooShort || tooLong ? styles.counterError : styles.counter
          }
        >
          {reason.length} / {REASON_MAX}
          {tooShort
            ? ` — minimum ${REASON_MIN} characters`
            : null}
        </span>
        <p className={styles.helper}>
          This will be recorded in the booking history.
        </p>
      </div>
    </Modal>
  );
}
